import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { layout } from '@modernhealth/vital-foundation';
import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    ComponentTextMedium,
    Form,
    FormInputTextarea,
    HeadingText50,
    renderMessage,
    StarRating,
    useForm,
} from '@modernhealth/vital-components';
import { Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { captureException } from '@sentry/browser';

import { CircleItemFeedbackType } from 'src/generated/webapp_gql';
import { ErrorFallback } from 'src/modules/dashboard/components/ErrorFallback';

import { ReactComponent as Celebration } from '../assets/tada.svg';
import { useCircleCreateItemFeedbackMutation } from '../documents/createItemFeedback.generated';
import { getErrorMessage } from '../utils/getErrorMessage';
import { CirclesOnDemandFeedbackMultiSelect } from '../components/CirclesOnDemandFeedbackMultiSelect';
import { remarks } from '../utils/feedbackRemarks';
import { useGetOnDemandCircleBySlugQuery } from '../documents/getOnDemandCircleBySlug.generated';

const Wrapper = styled.div({
    width: '100%',
    maxWidth: 498,
    margin: '0 auto',
    paddingTop: 48,
    paddingBottom: layout.footerHeight,
});

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

export type OndemandReview = { review: string | null };

interface ConfirmationProps {
    topicSlug: string;
}

interface FeedbackProps {
    itemId: string;
    rating: number;
    freeText: string;
    labels: string[];
}

export type FormData = {
    freeText: string;
    labels: string[];
    rating: number | null;
};

const schema = yup.object<FormData>().shape({
    rating: yup.number().min(1).max(5).nullable(),
    freeText: yup.string().max(5000),
    labels: yup.array().of(yup.string().oneOf(remarks.map(remark => remark.key))),
});

export const CirclesOnDemandFeedbackPage = () => {
    const { topicSlug } = useParams<ConfirmationProps>();
    const { data, loading, error } = useGetOnDemandCircleBySlugQuery({
        variables: { slug: topicSlug },
    });
    const { t } = useTranslation('circles');
    const history = useHistory();
    const [CircleCreateItemFeedbackMutation] = useCircleCreateItemFeedbackMutation();

    const formDefinition = useForm<FormData>({
        defaultValues: {
            freeText: '',
            labels: [],
            rating: null,
        },
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        if (error) {
            captureException(error);
            history.goBack();
        }
    }, [error, history]);

    if (loading) return null;

    const ondemand = data?.circleOnDemandBySlug;
    if (ondemand?.__typename !== 'CircleOnDemandNode') return <ErrorFallback />;
    const topicName = ondemand.name;
    const itemId = ondemand.id;

    const submitFeedback = async ({ itemId, rating, freeText, labels }: FeedbackProps) => {
        const { data } = await CircleCreateItemFeedbackMutation({
            variables: {
                data: {
                    type: CircleItemFeedbackType.OnDemandTopicFeedback,
                    itemId,
                    rating,
                    labels,
                    freeText,
                },
            },
        });

        const responseType = data?.circleCreateItemFeedback.__typename;

        if (responseType === 'CircleCreateItemFeedbackSuccessful') {
            history.replace(`/circles/on-demand/${topicSlug}/feedback/confirmation`);
        } else {
            renderMessage({
                content: t(getErrorMessage(responseType)),
                type: 'error',
                duration: 5,
            });
        }
    };

    const submitForm = (freeText: string, labels: string[], rating: number) => {
        if (starRating) {
            submitFeedback({
                itemId,
                freeText,
                rating,
                labels,
            });
        }
    };
    const starRating = formDefinition.watch('rating');
    return (
        <Wrapper>
            <Container>
                {!starRating ? (
                    <Container>
                        <Celebration height="12rem" width="17rem" />
                        <HeadingText50 marginBottom={24} centered>
                            {t('pages.on_demand_feedback.header')}
                        </HeadingText50>
                        <ComponentTextMedium marginBottom={16}>
                            {t('pages.on_demand_feedback.message', { topicName })}
                        </ComponentTextMedium>
                        <BodyTextMedium bold marginBottom={24}>
                            {t('pages.on_demand_feedback.question_primary')}
                        </BodyTextMedium>
                    </Container>
                ) : (
                    <HeadingText50 centered>
                        {t('pages.on_demand_feedback.question_secondary')}
                    </HeadingText50>
                )}
                <Form
                    onSubmit={({
                        freeText,
                        labels,
                        rating,
                    }: {
                        freeText: string;
                        labels: string[];
                        rating: number;
                    }) => {
                        submitForm(freeText, labels, rating);
                    }}
                    definition={formDefinition}
                >
                    <div
                        css={{ display: 'flex', justifyContent: 'center' }}
                        onClick={e => {
                            // StarRating is made of <button /> elements
                            // which will trigger the form submission if
                            // the click events bubble to <form />
                            e.preventDefault();
                            return false;
                        }}
                    >
                        <StarRating
                            numberOfStars={5}
                            onRateClick={stars => {
                                formDefinition.setValue('rating', stars);
                            }}
                            initialRate={starRating ?? 0}
                        />
                    </div>
                    {starRating && (
                        <>
                            <Controller
                                name="labels"
                                control={formDefinition.control}
                                render={({ field: { onChange, value } }) => {
                                    return (
                                        <CirclesOnDemandFeedbackMultiSelect
                                            onChange={onChange}
                                            labels={value}
                                        />
                                    );
                                }}
                            />
                            <FormInputTextarea
                                labelledBy="FormReview"
                                name="freeText"
                                placeholder={t('providers:request_provider.placeholder')}
                                rows={5}
                            />
                        </>
                    )}

                    <ButtonGroup>
                        <Button
                            size="large"
                            variant="secondary"
                            onClick={() => {
                                history.push(`/circles/on-demand/${topicSlug}/`);
                            }}
                        >
                            {t('pages.on_demand_feedback.button.secondary')}
                        </Button>
                        <Button size="large" variant="primary" type="submit" disabled={!starRating}>
                            {t('pages.on_demand_feedback.button.primary')}
                        </Button>
                    </ButtonGroup>
                </Form>
            </Container>
        </Wrapper>
    );
};
